import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import _LanguageConfigService from './LanguageConfigService';

// Phase C.3 (Candidate Runtime Language Integration & Legacy Reconciliation): PortalLanguage
// (Admin-controlled, Phase C.2/C.2.1 — EnterpriseConfiguration Category='PortalLanguage', read
// anonymously via LanguageConfigController.GetPortalLanguageConfig -> LanguageConfigService) is now the
// sole authoritative source for candidate locale/displayMode.
//
// The legacy mechanism this file used to read (EnterpriseConfiguration Category='Language' via
// EnterpriseConfigController.GetLanguageConfig -> EnterpriseOpsService.getLanguageConfig) is no longer
// consulted here. Per the Product Owner's explicit decision (see
// Stage_03.0/Reports/Language_Architecture_Decision_and_API_Contract.md), that legacy category's data is
// left completely untouched in the database — this file simply stops being the thing that reads it.
//
// Candidates can no longer choose or override the portal language (Phase C.3 Decision 2.1) —
// setLocale/setDisplayMode below are kept as harmless no-ops (not removed from the exported shape) so
// that any consumer this phase's discovery didn't find never crashes; AccessibilityToolbar.jsx no longer
// calls them (its language/display-mode selector controls were removed in this same phase).
//
// Still a Context/Provider (not a plain module like modules/branding.js) for the same reason as before:
// switching the effective policy must re-render visible text immediately without a full page reload.

const PORTAL_LANGUAGE_CACHE_KEY = 'ENT_PORTAL_LANGUAGE_CONFIG';

// Retired Stage 3.9 keys — no longer read for computing the effective candidate locale/displayMode.
// Removed (not merely ignored) once on Provider mount: each of these three keys existed for no purpose
// other than this exact mechanism, so clearing them is safe and prevents a stale, misleading candidate
// preference from lingering in a browser indefinitely once it can no longer have any effect (Phase C.3
// §12 — old browser language state must never override Admin configuration).
const RETIRED_KEYS = ['ENT_LANGUAGE_CONFIG', 'ENT_USER_LOCALE', 'ENT_USER_DISPLAY_MODE'];

const MODE_ENGLISH_PLUS_REGIONAL = 'EnglishPlusRegional';

// The one safe default (Phase C.3 §11): English Only. Never the legacy bilingual default — used both
// before the first successful fetch completes and if the fetch fails outright with no prior cache.
const SAFE_DEFAULT = { locale: 'en', displayMode: 'en', enabledLanguages: ['en'] };

function deriveFromPortalLanguage(config) {
  if (config && config.Mode === MODE_ENGLISH_PLUS_REGIONAL && config.RegionalLanguageCode) {
    return {
      locale: config.RegionalLanguageCode,
      displayMode: 'bilingual',
      enabledLanguages: ['en', config.RegionalLanguageCode]
    };
  }
  return SAFE_DEFAULT;
}

function readCachedPortalLanguage() {
  try {
    const cached = localStorage.getItem(PORTAL_LANGUAGE_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (e) {
    return null;
  }
}

// Plain, synchronous accessors — for class-component methods (setState-driven validation messages, e.g.
// Registration_new.jsx/CandidatePersonalInfo_new.jsx's onTextChange/validate()) that run outside React's
// render cycle and can't call the useLanguage() hook. Reads the same cache the Provider/hook use, so
// they always agree with whatever is currently rendered.
export function getCurrentLocale() {
  return deriveFromPortalLanguage(readCachedPortalLanguage()).locale;
}

export function getCurrentDisplayMode() {
  return deriveFromPortalLanguage(readCachedPortalLanguage()).displayMode;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [derived, setDerived] = useState(() => deriveFromPortalLanguage(readCachedPortalLanguage()));

  useEffect(() => {
    RETIRED_KEYS.forEach((key) => {
      try { localStorage.removeItem(key); } catch (e) { /* non-fatal */ }
    });

    let cancelled = false;
    (async () => {
      try {
        const response = await _LanguageConfigService.getPortalLanguageConfig();
        if (!cancelled && response && response.status === 200 && response.data) {
          localStorage.setItem(PORTAL_LANGUAGE_CACHE_KEY, JSON.stringify(response.data));
          setDerived(deriveFromPortalLanguage(response.data));
        }
      } catch (e) {
        // Presentation-only — never block app startup/rendering on a failed or anonymous-blocked fetch.
        // Leaves `derived` at whatever it was initialized to (a prior successful cache, or SAFE_DEFAULT
        // if none exists) — never the legacy bilingual default (Phase C.3 §11).
        console.log('Portal language config fetch skipped: ', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Phase C.3 Decision 2.1: candidates cannot override the Admin's language policy. Kept as no-ops
  // (rather than omitted from the value object) purely so an undiscovered caller never crashes.
  const setLocale = useCallback(() => {}, []);
  const setDisplayMode = useCallback(() => {}, []);

  const value = {
    locale: derived.locale,
    displayMode: derived.displayMode,
    enabledLanguages: derived.enabledLanguages,
    setLocale,
    setDisplayMode
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// Never throws — a consumer rendered without a Provider (e.g. an isolated future test) still gets a
// safe, sane default rather than a crash.
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      locale: SAFE_DEFAULT.locale,
      displayMode: SAFE_DEFAULT.displayMode,
      enabledLanguages: SAFE_DEFAULT.enabledLanguages,
      setLocale: () => {},
      setDisplayMode: () => {}
    };
  }
  return ctx;
}

export default LanguageContext;
